import React, { useCallback, useEffect, useRef, useState } from 'react'
import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import { getBigAssetPath, getMediumAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'
import {
  ASSETS_TYPES,
  assetsCollectionElement,
  IEcommerceAnswerSchema,
  IEcommerceVideoAsset,
  IFileAsset
} from '../../types.ts'
import { defaultAssetIndex } from '../../utils/defaultAssetIndex.ts'
import { getAssetType } from '../../utils/getAssetType.ts'
import style from './Gallery.module.scss'

interface IGalleryProps {
  answer: IEcommerceAnswerSchema
  assetsCollection: assetsCollectionElement[]
}

const PAN_DAMPING = 0.75

const Gallery = ({ answer, assetsCollection }: IGalleryProps) => {
  const { t } = useSurveyLocale()
  const noAltFallback = t('questions', 'img', 'noAlt')
  const carouselRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const thumbRefs = useRef<(HTMLButtonElement | null)[]>([])

  const [onImage, setOnImage] = useState<boolean>(false)
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null)
  const [selectedAssetIndex, setSelectedAssetIndex] = useState<number>(() => defaultAssetIndex(assetsCollection))

  // Read inside the mount effect without making it re-run on every asset change.
  const selectedAssetIndexRef = useRef(selectedAssetIndex)
  selectedAssetIndexRef.current = selectedAssetIndex

  const timeline = useTimeline()
  const viewport = useViewport()

  const playingIndexRef = useRef<number | null>(null)

  const logVideoEvent = (type: 'play_video' | 'pause_video', assetIndex: number) =>
    timeline.log({ type, ref: answer.id, target: assetIndex.toString(), context: ASSETS_TYPES.VIDEO.toLowerCase() })

  const onVideoPlay = (assetIndex: number) => {
    playingIndexRef.current = assetIndex
    logVideoEvent('play_video', assetIndex)
  }

  const onVideoPause = (assetIndex: number) => {
    // mobile mounts every slide's video at once — clear the ref only for the one it tracks
    if (playingIndexRef.current === assetIndex) playingIndexRef.current = null
    logVideoEvent('pause_video', assetIndex)
  }

  // Desktop unmounts the <video> on asset switch and both viewports lose it on gallery unmount,
  // so the element's own pause event never reaches React. Ref-based and idempotent.
  const flushPlayingVideo = () => {
    if (null === playingIndexRef.current) return
    logVideoEvent('pause_video', playingIndexRef.current)
    playingIndexRef.current = null
  }

  const currentAssetType = getAssetType(assetsCollection[selectedAssetIndex])
  const isImage = ASSETS_TYPES.IMAGE === currentAssetType
  const canZoom = isImage && !viewport.mobile

  const onMouseEnter = useCallback(() => {
    if (!canZoom || onImage) return
    timeline.log({ ref: answer.id, type: 'zoom_image_in', target: selectedAssetIndex.toString() })
    setOnImage(true)
  }, [canZoom, onImage, answer.id, selectedAssetIndex, timeline])

  const onMouseLeave = useCallback(() => {
    requestAnimationFrame(() => {
      if (onImage) {
        timeline.log({ ref: answer.id, type: 'zoom_image_out', target: selectedAssetIndex.toString() })
      }
      setOnImage(false)
      setPosition(null)
    })
  }, [onImage, answer.id, selectedAssetIndex, timeline])

  // Pan the background opposite the cursor so the point under it stays magnified. React 17
  // no longer pools events, so reading event.clientX inside the rAF callback is safe.
  const onMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onImage) return
      const element = event.currentTarget
      const { clientX, clientY } = event
      requestAnimationFrame(() => {
        const { left, top, width, height } = element.getBoundingClientRect()
        setPosition({
          x: -(clientX - left - width / 2) * PAN_DAMPING,
          y: -(clientY - top - height / 2) * PAN_DAMPING
        })
      })
    },
    [onImage]
  )

  // Scroll the strip only when the thumb sits outside it — a selection inside the visible window
  // leaves the strip where the respondent left it. Measured as rect deltas, not `offsetLeft`: the
  // strip is not the thumbs' offset parent, so their offsets are relative to an ancestor and
  // cannot be compared against the strip's own scroll position.
  const revealThumb = useCallback((index: number) => {
    const carousel = carouselRef.current
    const thumb = thumbRefs.current[index]
    if (!carousel || !thumb) return
    const strip = carousel.getBoundingClientRect()
    const box = thumb.getBoundingClientRect()
    if (box.left < strip.left) {
      carousel.scrollTo({ left: carousel.scrollLeft - (strip.left - box.left), behavior: 'smooth' })
    } else if (box.right > strip.right) {
      carousel.scrollTo({ left: carousel.scrollLeft + (box.right - strip.right), behavior: 'smooth' })
    }
  }, [])

  // The arrows step the selection, wrapping at either end; the strip follows only if it has to.
  const onStepAsset = useCallback(
    (direction: 1 | -1) => {
      const total = assetsCollection.length
      if (total < 2) return
      const next = (selectedAssetIndex + direction + total) % total
      setSelectedAssetIndex(next)
      revealThumb(next)
    },
    [assetsCollection.length, selectedAssetIndex, revealThumb]
  )

  const onTrackScroll = useCallback((event: React.UIEvent<HTMLElement>) => {
    const track = event.target as HTMLDivElement
    const index = Math.round(track.scrollLeft / track.offsetWidth)
    setSelectedAssetIndex(current => (index === current ? current : index))
  }, [])

  const scrollToIndex = useCallback((index: number) => {
    if (!trackRef.current) return
    trackRef.current.scrollTo({ left: trackRef.current.offsetWidth * index, behavior: 'auto' })
    setSelectedAssetIndex(index)
  }, [])

  // The default asset is not always the first one, so land the track on it before paint.
  useEffect(() => {
    if (!trackRef.current) return
    trackRef.current.scrollTo({ left: trackRef.current.offsetWidth * selectedAssetIndexRef.current, behavior: 'auto' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport.mobile])

  // Reset the magnifier whenever the shown asset changes.
  useEffect(() => {
    setOnImage(false)
    setPosition(null)
  }, [selectedAssetIndex])

  // Desktop only: the asset switch unmounts a playing video mid-play. Mobile slides stay mounted
  // and keep playing across swipes, so no flush there. Declared before the show_asset effect so
  // the closing pause lands ahead of the new asset's show_asset.
  useEffect(() => {
    if (!viewport.mobile) flushPlayingVideo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetIndex])

  useEffect(
    () => () => flushPlayingVideo(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    timeline.log({
      ref: answer.id,
      type: 'show_asset',
      target: selectedAssetIndex.toString(),
      context: (getAssetType(assetsCollection[selectedAssetIndex]) ?? '').toLowerCase()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetIndex])

  const activeAsset = assetsCollection[selectedAssetIndex]

  // Mobile — swipeable full-width track with dot pagination, no thumb rail and no magnifier
  // (touch has no hover, so the image renders flat). DD4.
  if (viewport.mobile) {
    return (
      <div className={`${style.gallery} ${style.mobile}`}>
        <div className={style.track} ref={trackRef} onScroll={onTrackScroll}>
          {assetsCollection.map((asset, index) => (
            <div className={style.slide} key={index}>
              {ASSETS_TYPES.IMAGE === getAssetType(asset) && (
                <div
                  className={style.slideImage}
                  role="img"
                  aria-label={(asset as IImageAsset).alt_text || noAltFallback}
                  style={{ backgroundImage: `url('${getMediumAssetPath(asset as IImageAsset)}')` }}
                />
              )}
              {ASSETS_TYPES.MODEL === getAssetType(asset) && (
                <model-viewer src={(asset as IFileAsset).file_url} camera-controls={false} />
              )}
              {ASSETS_TYPES.VIDEO === getAssetType(asset) && (
                <video
                  playsInline
                  className={style.videoAsset}
                  src={(asset as IEcommerceVideoAsset).video.url}
                  preload="metadata"
                  controls
                  onPlay={() => onVideoPlay(index)}
                  onPause={() => onVideoPause(index)}
                />
              )}
            </div>
          ))}
          {0 === assetsCollection.length && <div className={style.noImage} />}
        </div>

        {assetsCollection.length > 1 && (
          <div className={style.dots}>
            {assetsCollection.map((_, index) => (
              <button
                type="button"
                key={index}
                className={`${style.dot} ${index === selectedAssetIndex ? style.dotActive : ''}`}
                onClick={() => scrollToIndex(index)}
                aria-label={t<(index: number) => string>('questions', 'ec', 'kroger', 'showImage')(index + 1)}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={style.gallery}>
      <div className={style.mainImage} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {activeAsset ? (
          <>
            {isImage && (
              <div
                className={style.imageAsset}
                role="img"
                aria-label={(activeAsset as IImageAsset).alt_text || noAltFallback}
                style={{
                  backgroundImage: `url('${getBigAssetPath(activeAsset as IImageAsset)}')`,
                  backgroundPosition: position ? `calc(50% + ${position.x}px) calc(50% + ${position.y}px)` : '50% 50%',
                  transform: `scale(${onImage ? 2 : 1})`,
                  cursor: canZoom ? (onImage ? 'zoom-out' : 'zoom-in') : 'default'
                }}
                onMouseMove={onMouseMove}
              />
            )}
            {ASSETS_TYPES.MODEL === currentAssetType && (
              <model-viewer src={(activeAsset as IFileAsset).file_url} camera-controls={false} />
            )}
            {ASSETS_TYPES.VIDEO === currentAssetType && (
              <video
                playsInline
                className={style.videoAsset}
                src={(activeAsset as IEcommerceVideoAsset).video.url}
                preload="metadata"
                controls
                onPlay={() => onVideoPlay(selectedAssetIndex)}
                onPause={() => onVideoPause(selectedAssetIndex)}
              />
            )}
            {canZoom && !onImage && (
              <span className={style.zoomHint}>
                <i className="pi pi-search" />
              </span>
            )}
          </>
        ) : (
          <div className={style.noImage} />
        )}
      </div>

      {assetsCollection.length > 1 && (
        <div className={style.carousel}>
          <button
            type="button"
            className={style.navButton}
            onClick={() => onStepAsset(-1)}
            aria-label={t('questions', 'ec', 'kroger', 'previousImage')}
          >
            <i className="pi pi-chevron-left" />
          </button>
          <div className={style.thumbs} ref={carouselRef}>
            {assetsCollection.map((asset, index) => (
              <button
                type="button"
                key={index}
                ref={node => {
                  thumbRefs.current[index] = node
                }}
                className={`${style.thumb} ${index === selectedAssetIndex ? style.thumbActive : ''}`}
                onClick={() => setSelectedAssetIndex(index)}
              >
                {ASSETS_TYPES.IMAGE === getAssetType(asset) && (
                  <span
                    className={style.thumbImage}
                    role="img"
                    aria-label={(asset as IImageAsset).alt_text || noAltFallback}
                    style={{ backgroundImage: `url('${getSmallAssetPath(asset as IImageAsset)}')` }}
                  />
                )}
                {ASSETS_TYPES.MODEL === getAssetType(asset) && (
                  <span className={style.thumbFile}>
                    <model-viewer
                      src={(asset as IFileAsset).file_url}
                      camera-controls={false}
                      interaction-prompt="none"
                    />
                    <span className={style.thumbLayer} />
                  </span>
                )}
                {ASSETS_TYPES.VIDEO === getAssetType(asset) && (
                  <span
                    className={style.thumbImage}
                    role="presentation"
                    style={{ backgroundImage: `url('${(asset as IEcommerceVideoAsset).video.thumbnail}')` }}
                  />
                )}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={style.navButton}
            onClick={() => onStepAsset(1)}
            aria-label={t('questions', 'ec', 'kroger', 'nextImage')}
          >
            <i className="pi pi-chevron-right" />
          </button>
        </div>
      )}
    </div>
  )
}

export default Gallery
